import React from 'react';
import SectionLabel from './SectionLabel';

const services = [
  {
    title: 'Brand & Positioning System',
    desc: 'We establish what your brand stands for, how it communicates, and how it should be perceived — then codify it into a system that holds across every channel and touchpoint.',
    tags: ['Brand Strategy', 'Visual Identity', 'Positioning', 'Messaging'],
  },
  {
    title: 'Content & Social System',
    desc: 'Strategic content architecture designed to build authority, attract the right audience, and compound over time. Includes platform management, content planning, creation, and scheduling.',
    tags: ['Social Management', 'Content Strategy', 'Content Creation', 'Remote Direction'],
  },
  {
    title: 'Paid Growth Infrastructure',
    desc: 'We build and manage paid advertising systems on Meta, Google, and TikTok — with proper funnel structure, audience architecture, and conversion systems behind every campaign.',
    tags: ['Meta Ads', 'Google Ads', 'TikTok Ads', 'Funnel Design'],
  },
  {
    title: 'Website & Conversion System',
    desc: 'Premium website design and development built around conversion — not aesthetics alone. Every page, CTA, and flow is engineered to turn visitors into qualified leads or buyers.',
    tags: ['Web Design', 'Conversion Design', 'Landing Pages', 'UX'],
  },
  {
    title: 'Retention & Email System',
    desc: 'Email and SMS marketing infrastructure that builds loyalty, drives repeat revenue, and keeps your audience engaged between purchase moments. Built with strategy, not noise.',
    tags: ['Email Marketing', 'SMS Marketing', 'Retention Strategy', 'Automation'],
  },
  {
    title: 'Operations & Growth Diagnostics',
    desc: 'We map your existing backend, identify structural weaknesses, and build the operational frameworks — CRM, workflow, follow-up systems — that allow your business to scale without breaking.',
    tags: ['CRM Setup', 'Workflow Design', 'Systems Audit', 'Advisory'],
  },
];

const cardBase: React.CSSProperties = {
  background: '#fefcf5',
  borderRadius: 3,
  padding: '36px 30px 32px',
  border: '1px solid rgba(184,154,90,0.16)',
  boxShadow: '0 2px 12px rgba(139,110,50,0.07), 0 1px 3px rgba(139,110,50,0.05)',
  cursor: 'default',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.28s cubic-bezier(0.22,1,0.36,1), box-shadow 0.28s cubic-bezier(0.22,1,0.36,1), border-color 0.28s ease, background 0.25s ease',
};

const cardHover: React.CSSProperties = {
  transform: 'translateY(-8px)',
  background: '#ffffff',
  borderColor: 'rgba(184,154,90,0.38)',
  boxShadow: '0 20px 48px rgba(139,110,50,0.16), 0 6px 16px rgba(139,110,50,0.1)',
};

const ServiceCard: React.FC<typeof services[0]> = ({ title, desc, tags }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      style={hovered ? { ...cardBase, ...cardHover } : cardBase}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Gold reveal bar — slides in from left on hover */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #b89a5a, #d4b87a)',
          transform: hovered ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left',
          transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1)',
        }}
      />

      {/* Accent line — extends on hover */}
      <div
        style={{
          width: hovered ? 52 : 32,
          height: 1,
          background: '#b89a5a',
          marginBottom: 24,
          transition: 'width 0.3s ease',
        }}
      />

      <h3
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 20,
          fontWeight: 400,
          color: hovered ? '#8a6820' : '#1a130a',
          marginBottom: 12,
          lineHeight: 1.25,
          letterSpacing: '-0.01em',
          transition: 'color 0.25s ease',
        }}
      >
        {title}
      </h3>

      <p
        style={{
          fontSize: 11.5,
          fontWeight: 300,
          color: '#6b5f46',
          lineHeight: 2,
          letterSpacing: '0.02em',
          marginBottom: 20,
        }}
      >
        {desc}
      </p>

      <div className="flex flex-wrap gap-[6px]">
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 8.5,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#8a7040',
              border: `1px solid ${hovered ? 'rgba(184,154,90,0.45)' : 'rgba(184,154,90,0.25)'}`,
              padding: '4px 9px',
              borderRadius: 1,
              background: hovered ? 'rgba(184,154,90,0.06)' : 'transparent',
              transition: 'background 0.2s, border-color 0.2s',
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
};

const Services: React.FC = () => (
  <section
    id="services"
    className="relative py-[120px] px-10"
    style={{
      background: 'linear-gradient(158deg, #faf4e8 0%, #f5ecce 38%, #ede0b4 72%, #e8d89e 100%)',
      borderTop: '1px solid rgba(184,154,90,0.18)',
    }}
  >
    {/* Ambient glows */}
    <div
      className="absolute pointer-events-none"
      style={{
        top: -100, left: -100,
        width: 420, height: 420,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(184,154,90,0.1) 0%, transparent 70%)',
      }}
    />
    <div
      className="absolute pointer-events-none"
      style={{
        bottom: -120, right: -80,
        width: 400, height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(184,154,90,0.08) 0%, transparent 70%)',
      }}
    />

    <div className="max-w-[1200px] mx-auto relative z-10">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-20 mb-[64px] items-end">
        <div>
          <SectionLabel>What We Build</SectionLabel>
          <h2
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: 'clamp(34px, 4.5vw, 58px)',
              fontWeight: 300,
              lineHeight: 1.08,
              color: '#1a130a',
            }}
          >
            Six Systems.
            <br />
            <em style={{ fontStyle: 'italic', color: '#b89a5a' }}>One Direction.</em>
          </h2>
        </div>
        <p
          style={{
            fontSize: 13,
            fontWeight: 300,
            color: '#5a4e38',
            lineHeight: 2,
            letterSpacing: '0.02em',
          }}
        >
          We don't offer a menu of disconnected services. Every system we build is
          designed to integrate — brand, content, growth, and operations functioning
          as one coherent architecture.
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 22 }}>
        {services.map((s) => (
          <ServiceCard key={s.title} {...s} />
        ))}
      </div>
    </div>
  </section>
);

export default Services;